import { createClient } from "@/lib/supabase/client";

export interface ProductAttribute {
  id: string;
  name: string;
  description: string | null;
  display_order: number;
  filterable: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProductAttributeValue {
  id: string;
  attribute_id: string;
  value: string;
  display_order: number;
  created_at: string;
  updated_at: string;
  attribute?: ProductAttribute;
}

export interface ProductVariant {
  id: string;
  product_id: number;
  sku: string | null;
  price: number;
  sale_price: number | null;
  stock: number;
  is_default: boolean;
  created_at: string;
  updated_at: string;
  isNew?: boolean;
  attributes?: ProductVariantAttribute[];
}

export interface ProductVariantAttribute {
  id: string;
  variant_id: string;
  attribute_id: string;
  attribute_value_id: string;
  attribute_value?: {
    id: string;
    value: string;
    attribute_id: string;
    display_order: number;
    created_at: string;
    updated_at: string;
    attribute?: {
      id: string;
      name: string;
      description: string | null;
      display_order: number;
      filterable: boolean;
      created_at: string;
      updated_at: string;
    };
  };
}

export interface AttributeWithValues extends ProductAttribute {
  values: ProductAttributeValue[];
}

// Helper function to fetch all product attributes with their values
export async function fetchProductAttributes(): Promise<AttributeWithValues[]> {
  const supabase = createClient();
  
  // Fetch all attributes
  const { data: attributes, error: attributesError } = await supabase
    .from('product_attributes')
    .select('*')
    .order('display_order', { ascending: true });
    
  if (attributesError) {
    console.error('Error fetching product attributes:', attributesError);
    return [];
  }
  
  // Fetch all attribute values
  const { data: attributeValues, error: valuesError } = await supabase
    .from('product_attribute_values')
    .select('*')
    .order('display_order', { ascending: true });
    
  if (valuesError) {
    console.error('Error fetching attribute values:', valuesError);
    return [];
  }
  
  // Group values by attribute
  const attributesWithValues = attributes.map((attribute: ProductAttribute) => {
    const values = attributeValues.filter((value: ProductAttributeValue) => value.attribute_id === attribute.id);
    return {
      ...attribute,
      values
    };
  });
  
  return attributesWithValues;
}

// Helper function to fetch product variants for a specific product
export async function fetchProductVariants(productId: number): Promise<ProductVariant[]> {
  const supabase = createClient();
  
  // Fetch variants for the product
  const { data: variants, error: variantsError } = await supabase
    .from('product_variants')
    .select('*')
    .eq('product_id', productId)
    .order('is_default', { ascending: false });
    
  if (variantsError) {
    console.error('Error fetching product variants:', variantsError);
    return [];
  }
  
  // If no variants, return empty array
  if (!variants || variants.length === 0) {
    return [];
  }
  
  // Fetch variant attributes
  const variantIds = variants.map((variant: ProductVariant) => variant.id);
  const { data: variantAttributes, error: attributesError } = await supabase
    .from('product_variant_attributes')
    .select(`
      *,
      attribute_value:attribute_value_id (
        *,
        attribute:attribute_id (*)
      )
    `)
    .in('variant_id', variantIds);
    
  if (attributesError) {
    console.error('Error fetching variant attributes:', attributesError);
    return variants;
  }
  
  // Merge variant attributes with variants
  const variantsWithAttributes = variants.map((variant: ProductVariant) => {
    const attributes = variantAttributes.filter((attr: ProductVariantAttribute) => attr.variant_id === variant.id);
    return {
      ...variant,
      attributes
    };
  });
  
  return variantsWithAttributes;
}

// Helper function to create a new variant for a product
export async function createProductVariant(variant: Omit<ProductVariant, 'id' | 'created_at' | 'updated_at'>, attributeValueIds: string[]): Promise<{ success: boolean, variant?: ProductVariant, error?: any }> {
  const supabase = createClient();
  
  try {
    // Create the variant
    const { data: newVariant, error: variantError } = await supabase
      .from('product_variants')
      .insert({
        product_id: variant.product_id,
        sku: variant.sku,
        price: variant.price,
        sale_price: variant.sale_price,
        stock: variant.stock,
        is_default: variant.is_default
      })
      .select()
      .single();
      
    if (variantError) throw variantError;
    
    // Create the variant attribute associations
    const variantAttributes = attributeValueIds.map(valueId => ({
      variant_id: newVariant.id,
      attribute_value_id: valueId
    }));
    
    const { error: attributesError } = await supabase
      .from('product_variant_attributes')
      .insert(variantAttributes);
      
    if (attributesError) throw attributesError;
    
    // Update the product to indicate it has variants
    const { error: productError } = await supabase
      .from('products')
      .update({ has_variants: true })
      .eq('id', variant.product_id);
      
    if (productError) throw productError;
    
    return { success: true, variant: newVariant };
  } catch (error) {
    console.error('Error creating product variant:', error);
    return { success: false, error };
  }
}

// Helper function to update a product variant
export async function updateProductVariant(variant: ProductVariant, attributeValueIds: string[]): Promise<{ success: boolean, error?: any }> {
  const supabase = createClient();
  
  try {
    // Update the variant
    const { error: variantError } = await supabase
      .from('product_variants')
      .update({
        sku: variant.sku,
        price: variant.price,
        sale_price: variant.sale_price,
        stock: variant.stock,
        is_default: variant.is_default
      })
      .eq('id', variant.id);
      
    if (variantError) throw variantError;
    
    // Delete existing attribute associations
    const { error: deleteError } = await supabase
      .from('product_variant_attributes')
      .delete()
      .eq('variant_id', variant.id);
      
    if (deleteError) throw deleteError;
    
    // Create new attribute associations
    const variantAttributes = attributeValueIds.map(valueId => ({
      variant_id: variant.id,
      attribute_value_id: valueId
    }));
    
    const { error: attributesError } = await supabase
      .from('product_variant_attributes')
      .insert(variantAttributes);
      
    if (attributesError) throw attributesError;
    
    return { success: true };
  } catch (error) {
    console.error('Error updating product variant:', error);
    return { success: false, error };
  }
}

// Helper function to delete a product variant
export async function deleteProductVariant(variantId: string, productId: number): Promise<{ success: boolean, error?: any }> {
  const supabase = createClient();
  
  try {
    // Delete the variant (cascade will delete attribute associations)
    const { error: deleteError } = await supabase
      .from('product_variants')
      .delete()
      .eq('id', variantId);
      
    if (deleteError) throw deleteError;
    
    // Check if this was the last variant
    const { data: remainingVariants, error: countError } = await supabase
      .from('product_variants')
      .select('id')
      .eq('product_id', productId);
      
    if (countError) throw countError;
    
    // If no variants remain, update the product
    if (!remainingVariants || remainingVariants.length === 0) {
      const { error: updateError } = await supabase
        .from('products')
        .update({ has_variants: false })
        .eq('id', productId);
        
      if (updateError) throw updateError;
    }
    
    return { success: true };
  } catch (error) {
    console.error('Error deleting product variant:', error);
    return { success: false, error };
  }
}

// Format attribute values for display
export function formatVariantAttributes(attributes: ProductVariantAttribute[]): string {
  if (!attributes || attributes.length === 0) return '';
  
  return attributes
    .map(attr => {
      if (!attr.attribute_value?.attribute) return '';
      return `${attr.attribute_value.attribute.name}: ${attr.attribute_value.value}`;
    })
    .filter(Boolean)
    .join(' | ');
}

// Get attribute values for a specific attribute ID from all variants
export function getGroupedAttributeValues(attributeId: string, variants: ProductVariant[]): ProductAttributeValue[] {
  const uniqueValues = new Map<string, ProductAttributeValue>();
  
  variants.forEach(variant => {
    variant.attributes?.forEach(attr => {
      if (attr.attribute_value?.attribute?.id === attributeId) {
        if (attr.attribute_value && !uniqueValues.has(attr.attribute_value.id)) {
          uniqueValues.set(attr.attribute_value.id, attr.attribute_value);
        }
      }
    });
  });
  
  return Array.from(uniqueValues.values());
}

// Find a variant based on selected attribute values
export function findVariantByAttributes(
  variants: ProductVariant[],
  selectedAttributeValues: Record<string, string>
): ProductVariant | null {
  // Convert selectedAttributeValues to an array of value IDs
  const selectedValueIds = Object.values(selectedAttributeValues);
  
  // Find a variant that has all the selected attributes
  return variants.find(variant => {
    if (!variant.attributes) return false;
    
    // Get all attribute value IDs for this variant
    const variantValueIds = variant.attributes.map(attr => attr.attribute_value_id);
    
    // Check if all selected value IDs are in this variant
    return selectedValueIds.every(valueId => variantValueIds.includes(valueId));
  }) || null;
}

// Generate SQL conditions for filtering products by attribute values
export function generateAttributeFilterConditions(
  attributeFilters: Record<string, string[]>
): string {
  const attributeEntries = Object.entries(attributeFilters);
  
  // If no attribute filters, return empty string
  if (attributeEntries.length === 0) {
    return '';
  }
  
  // For each attribute-value combination, find all products that have variants with those attributes
  const attributeConditions = attributeEntries.map(([attributeId, valueIds]) => {
    if (valueIds.length === 0) return null;
    
    // Format the value IDs for SQL
    const valuesList = valueIds.map(id => `'${id}'`).join(',');
    
    // Find products where at least one variant has the specified attribute-value combination
    return `id IN (
      SELECT DISTINCT product_variants.product_id 
      FROM product_variants 
      JOIN product_variant_attributes ON product_variants.id = product_variant_attributes.variant_id 
      JOIN product_attribute_values ON product_variant_attributes.attribute_value_id = product_attribute_values.id
      WHERE product_attribute_values.attribute_id = '${attributeId}'
      AND product_variant_attributes.attribute_value_id IN (${valuesList})
    )`;
  }).filter(Boolean);
  
  // Combine all attribute conditions with AND logic so products must match ALL attribute filters
  return attributeConditions.join(' AND ');
}
